/**
 * GUC Sizer v2 — the vocabularies and the one prefilled exception.
 *
 * Source: eastview-os `frontend_documentation/GUC_SIZER_V2_FRONTEND_SPEC.md`.
 * Every option value below is a VERBATIM backend string or a real model bound
 * (`interestReserveMonths` is `ge=1, le=12` on `SubjectProperty`; the term list
 * is the rate sheet's own `termAdjustments` keys). Labels may differ from
 * values; values may not — they are matched server-side by string equality.
 */

/**
 * `pipelineTarget`, said the way the desk says it.
 *
 * The wire values are COLLECTION NAMES (`underwriting_pipeline` /
 * `loan_acquisitions`, `PIPELINE_TARGET_OPTIONS` in `pricing_scenarios.py`) and
 * the spec is explicit that they never reach the screen: what the user is
 * choosing is whether this is a deal we are originating or a loan we are buying.
 */
export const PIPELINE_LABELS = {
  underwriting_pipeline: "Pre-Close",
  loan_acquisitions: "Loan Trade",
};

export const gucOptionsPipelineTarget = Object.entries(PIPELINE_LABELS).map(
  ([value, label]) => ({ value, label }),
);

/**
 * GUC terms. The four the RTL rate sheet actually prices — `termAdjustments`
 * carries rows for 12 / 18 / 24 / 36 and nothing else, so offering a term with
 * no adjustment row would quietly price at +0.00%.
 */
export const gucOptionsTermMonths = [12, 18, 24, 36].map((v) => ({
  value: v,
  label: `${v} months`,
}));

/**
 * Permits & plans — THREE states, and the labels say why.
 *
 * `apply_leverage_reductions` fires `no_permits_and_plans_front_end` (−10%,
 * front-end scoped) only on an explicit `false`. An unanswered field is not a
 * "no" and takes no reduction, so the control has to be able to express that.
 */
export const gucOptionsApprovedPermits = [
  { value: true, label: "Yes — permits and plans approved" },
  { value: false, label: "No — takes the −10% front-end reduction" },
];

/**
 * Findings that are NOTES, not failures.
 *
 * All three ship `severity: 'info'` and each is already a complete sentence
 * about a cap that is working as designed. They are never red and they never
 * carry a Request-exception button — there is nothing to except.
 */
export const INFO_FINDING_CODES = [
  "feasibility_analysis_required",
  "weak_credit_front_end_cap",
  "tltc_capped",
];

export const isInfoFinding = (f) =>
  f?.severity === "info" || INFO_FINDING_CODES.includes(f?.code);

/**
 * The compensating factors a GC-completion-guaranty request opens with.
 *
 * Seeded, not asserted: they are the structure the desk actually offers on a
 * zero-track-record ground-up deal, and the requester edits them before filing.
 */
export const GUC_GUARANTY_COMP_SEED = [
  "General Contractor provides a completion guaranty on the project.",
  "GC is licensed and insured with verifiable completed ground-up projects.",
  "Overall leverage reduced against the class grid.",
  "Construction draws administered against third-party inspections.",
].join("\n");

/**
 * The blocker this guaranty answers.
 *
 * A ground-up scenario classifies on completed-project experience alone
 * (`ClassificationConfig.classify`, thresholds A+ 4 / A 2 / B 1, B also needing
 * FICO 700+). With none, the borrower resolves to class C — and the GUC
 * leverage grid HAS NO CLASS C ROW, so every maximum comes back null and the
 * deal has no credit box at all rather than a low one. That is the state this
 * detects; it is not something the engine raises as an exception, because there
 * is no threshold left to breach.
 */
export const isGucZeroExperienceBlock = (result) => {
  if (!result) return false;
  const cls = result.resolved?.borrowerClass || result.resolved?.classification?.borrowerClass;
  if (cls !== "C") return false;
  const rows = result.leverage || [];
  return rows.length > 0 && rows.every((r) => r.maximum == null);
};

/**
 * The prefilled request the guaranty toggle opens.
 *
 * Shaped for `POST /pricing/scenarios/{id}/exception-requests`, which takes the
 * same object a deal exception does (`build_scenario_exception`) — so an
 * approval granted here does not have to be granted again once the scenario
 * becomes a loan.
 */
export const gucGuarantyExceptionPrefill = ({ result, values } = {}) => {
  const exp = result?.resolved?.experience?.value ?? 0;
  const thresholds = "A+ 4 / A 2 / B 1 completed ground-up projects";
  return {
    severity: "hard",
    code: "guc_no_ground_up_experience",
    exceptionCategory: "Borrower",
    riskBucket: "Borrower Profile",
    exceptionDescription:
      `Ground-up construction with ${exp || 0} completed ground-up project(s). ` +
      `GUC classifies on completed-project experience (${thresholds}), so this ` +
      `borrower resolves to class C and the GUC leverage grid carries no class C ` +
      `row — the scenario has no credit box rather than a reduced one. ` +
      `Requesting approval to structure with a General Contractor completion ` +
      `guaranty at reduced overall leverage.`,
    compensatingFactors: GUC_GUARANTY_COMP_SEED,
    observedValue: exp || 0,
    threshold: thresholds,
    citation: result?.classExplanation || null,
    requiredDocuments: [
      "Completion Guaranty",
      "Construction Contract",
      "Contractor License",
      "Contractor Profile Form",
    ],
    // Carried for the approver's context; the endpoint keeps unknown keys off
    // the row, so this is a note on the request, not a claim about the loan.
    pricingCategory: values?.loanProductType || "Ground Up Construction",
  };
};

/**
 * Exception requests that are FILED AND STILL OPEN, as a set of codes.
 *
 * The scenario doc carries every request in `exceptionRequests[]` with the
 * engine's own `exceptionCode` on it, so a finding row can ask "has this one
 * already been sent?" without a second endpoint. Approved and Rejected both
 * leave the pending state — an approved blocker is cleared, a rejected one is
 * back to being a blocker.
 */
export const pendingExceptionCodes = (scenarioDoc) => {
  const rows = scenarioDoc?.exceptionRequests || [];
  return rows
    .filter(
      (r) => r && !["Approved", "Rejected", "No Longer Applies"].includes(r.exceptionStatus),
    )
    .map((r) => r.exceptionCode)
    .filter(Boolean);
};
